import "../../stylesheets/shared-package/shared.less";
import "../../stylesheets/video-package/video.less";

import {t} from "@lingui/core/macro";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";

import PageRoot from "#app-shell-package/page-root.tsx";
import PageDisplay from "#data-access/page-display.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

import Attribution from "./attribution.tsx";
import {withStrings} from "./locales/embed-video-page/load-strings.tsx";
import QueryEmbeddedVideo from "./query-embedded-video.tsx";
import StatefulVideoWrapper from "./stateful-video-wrapper.tsx";

type Props = WithLocationProps;

/**
 * Generate the Google Analytics code for the given video.
 *
 * Analytics may not be enabled (such as during our testing) so we have to cope
 * with that scenario.
 */
const makeAnalyticsScript = (
    youTubeId: string,
): string => `if (typeof ga !== "undefined") {
ga('send', 'event', 'Embedded-Player-View', '${youTubeId}', document.referrer || '');
ga('secondTracker.send', 'event', 'Embedded-Player-View', '${youTubeId}', document.referrer || '');
}`;

/**
 * Render a video widget for embedding in other pages.
 */
const EmbedVideoPage = (
    props: Props,
): React.ReactElement => ( // internal_video_only: from `internal_video_only` param
    <PageRoot
        settings={{
            footer: false,
            header: false,
        }}
    >
        <View aria-label={t`Khan Academy video wrapper`}>
            <QueryEmbeddedVideo>
                {(video) => (
                    <PageDisplay
                        resetSettings={false}
                        settings={{
                            title: video.translatedTitle,
                            inlineScripts: [
                                makeAnalyticsScript(video.youtubeId),
                            ],
                        }}
                    >
                        <StatefulVideoWrapper
                            video={video}
                            embedded={true}
                            task={null}
                        />
                        {!props.location.queryParams.internal_video_only && (
                            <Attribution url={video.kaUrl} />
                        )}
                    </PageDisplay>
                )}
            </QueryEmbeddedVideo>
        </View>
    </PageRoot>
);

type ExportProps = WithoutLocation<
    JSX.LibraryManagedAttributes<
        typeof EmbedVideoPage,
        React.ComponentProps<typeof EmbedVideoPage>
    >
>;

export default withStrings()(
    withLocation(EmbedVideoPage) as React.ComponentType<ExportProps>,
);
